import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, Instagram, Check } from "lucide-react";
import { api } from "@/lib/api";

const TOUR_HERO_IMAGE = "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/oivt9a3p__L2A0180.jpg";

// Swap this for the official Metamorph YouTube video ID once it drops.
// Currently set to "The Hill" so the embed always shows something playable.
const YOUTUBE_VIDEO_ID = "xm-4MQdbrT4";

/**
 * `/shows/:city` — the dedicated show-overview page that QR codes on the
 * physical posters lead to. Designed to be the warmest possible first
 * impression for someone scanning a poster at a venue: hero image, brief
 * "about the show + about the artist" copy, and a single Get-Tickets CTA.
 */
export default function ShowOverview() {
  const { city } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/shows");
        if (!alive) return;
        // URL slugs may use either hyphen or underscore — canonical key is underscore.
        const key = (city || "").toLowerCase().replace(/-/g, "_");
        const found = (data.shows || []).find((s) => s.city === key);
        setData(found || { notFound: true });
      } catch {
        if (alive) setData({ notFound: true });
      }
    })();
    return () => { alive = false; };
  }, [city]);

  if (!data) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-[var(--mm-bg,#050a1a)] text-white px-6" data-testid="show-loading">
        <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-white/55">Loading…</p>
      </main>
    );
  }

  if (data.notFound) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center bg-[var(--mm-bg,#050a1a)] text-white px-6 py-12" data-testid="show-not-found">
        <h1 className="deco-text text-3xl uppercase mb-4" style={{ letterSpacing: "0.08em" }}>Show not found</h1>
        <Link to="/tickets" className="btn-line inline-flex">View all shows →</Link>
      </main>
    );
  }

  const onSale = data.status === "on_sale";
  const soldOut = onSale && data.remaining <= 0;
  const isPrivate = !!data.private;

  return (
    <main className="min-h-screen bg-[var(--mm-bg,#050a1a)] text-white" data-testid="show-overview">
      {/* Hero image */}
      <div className="relative w-full aspect-[3/2] sm:aspect-[16/9] max-h-[70vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${TOUR_HERO_IMAGE})` }}
          data-testid="show-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--mm-bg,#050a1a)] via-transparent to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10">
          <h1
            className="deco-text text-5xl sm:text-7xl uppercase leading-[0.95] mb-1"
            style={{ letterSpacing: "0.1em" }}
            data-testid="show-title"
          >
            Metamorph
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/55 mb-3">
            By George Maple
          </p>
          <h2
            className="text-2xl sm:text-3xl mb-4"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
            data-testid="show-city"
          >
            {data.label}
          </h2>
          {onSale && !soldOut && !isPrivate && (
            <Link
              to={`/buy/${data.buy_slug}`}
              className="btn-line inline-flex text-sm py-2.5"
              data-testid="show-cta-tickets-hero"
            >
              Buy tickets &mdash; {data.currency_symbol}{Math.round(data.price)} <ArrowRight className="w-4 h-4" strokeWidth={1.4} />
            </Link>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        {/* Show details */}
        <div className="flex flex-col gap-2 mb-10 pb-10 border-b border-white/15">
          {data.rescheduled && (
            <span className="font-mono text-[9.5px] uppercase tracking-[0.32em] text-[#f3e0a6] border border-[#f3e0a6]/40 px-2 py-0.5 self-start" data-testid="show-rescheduled-chip">
              ** New date
            </span>
          )}
          {data.date && (
            <p className="flex items-center gap-2 text-[14px] text-white/85" data-testid="show-date">
              <Calendar className="w-4 h-4" strokeWidth={1.4} /> {data.date}
            </p>
          )}
          {data.venue && (
            <p className="flex items-center gap-2 text-[14px] text-white/85" data-testid="show-venue">
              <MapPin className="w-4 h-4" strokeWidth={1.4} /> {data.venue}
              {data.address && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${data.venue}, ${data.address}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 ml-1 hover:text-[var(--mm-accent)] underline-offset-4 hover:underline"
                  data-testid="show-venue-map"
                >
                  · {data.address}
                </a>
              )}
            </p>
          )}
          {data.rescheduled && (
            <p className="text-[11px] italic text-white/45 leading-relaxed mt-3" data-testid="rsv-disclaimer">
              ** GM has come down with RSV, a respiratory virus &mdash; new dates have been rescheduled and tickets issued.
            </p>
          )}
        </div>

        {/* About the show */}
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-3">About</p>
        <div
          className="text-[15px] leading-[1.85] text-white/85 space-y-5 mb-12"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400 }}
          data-testid="show-overview-copy"
        >
          <p>
            George Maple is an Australian-born, multidisciplinary artist who moves fluidly
            across genres and mediums &mdash; film, music and live performance. Her work
            lives somewhere between the subterranean depths of the human inner world and the
            esoteric, unseen realms of the metaphysical, with threads of fantasy woven
            through. Her voice captivates, drawing you in with a rare ability to float
            effortlessly between power and vulnerability.
          </p>
          <p>
            You may know George from her own work, namely &lsquo;Talk Talk&rsquo;, later
            reworked by DJ Snake into &lsquo;Talk&rsquo;. You may also know her prolific
            catalogue of work for other artists &mdash; a true songwriter at heart, she has
            lent her writing and production to artists including Melanie C, Anna Lunoe,
            Flume, Hayden James, What So Not, Tkay Maidza, Flight Facilities, Jessica
            Mauboy and DJ Snake, quietly amassing over 1 billion streams and
            multiple award nominations along the way.
          </p>
          <p>
            As a performer, George has taken the stage all over the world &mdash; from
            Carmen in Paris and The Enmore in Sydney to guest appearances at Coachella,
            Lollapalooza and EDC. She has built private conceptual performances for Cartier
            and Dom P&eacute;rignon, and earned recognition for her filmmaking, including
            the ACS National Gold Award. In 2020, she screened a self-written and directed
            24-minute live-performance film at the Sydney Opera House.
          </p>
          <p>
            After time away from music to focus on filmmaking,
            George returns to the stage to celebrate the release of her new piece of music,
            &lsquo;<em style={{ color: "#f3e0a6", fontStyle: "italic" }}>Metamorph</em>&rsquo;
            &mdash; performed alongside reworked moments from her discography, in
            hand-picked venues across the globe.
          </p>
          <p>
            Each performance George creates is unique, built specifically for its
            environment.
          </p>
          <p
            className="italic border-l-2 pl-5"
            style={{ borderColor: "rgba(243,224,166,0.45)", color: "#f3e0a6" }}
          >
            In her words &mdash; &ldquo;Metamorph felt like the perfect offering to share
            with the world. It&rsquo;s a small slice of the inner realm of my own psyche. I
            wanted these performances to be luscious and deeply connected &mdash; each one
            will feel like we&rsquo;re all in a moody living room together.&rdquo;
          </p>
          <p>
            <em style={{ color: "#f3e0a6", fontStyle: "italic" }}>Metamorph</em> will be
            available exclusively on 12&Prime; Vinyl and direct digital download for the
            month of July.
          </p>
        </div>

        {/* Watch — YouTube embed (no login required). Engagement point #1.
            Swap YOUTUBE_VIDEO_ID at the top of this file for the official
            Metamorph video when it goes live. */}
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-2">Watch</p>
        <p
          className="text-[15px] text-white/75 mb-5"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400 }}
          data-testid="show-watch-lead"
        >
          Press play on &lsquo;The Hill&rsquo; &mdash; from George&rsquo;s discography.
        </p>
        <div
          className="border border-white/15 mb-6 overflow-hidden"
          data-testid="show-audio-player"
          style={{ aspectRatio: "16 / 9" }}
        >
          <iframe
            title="George Maple — watch"
            src={`https://www.youtube-nocookie.com/embed/${YOUTUBE_VIDEO_ID}?rel=0&modestbranding=1&color=white`}
            width="100%"
            height="100%"
            frameBorder="0"
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            loading="lazy"
            style={{ display: "block", border: 0 }}
          />
        </div>
        <p className="text-center mb-8">
          <a
            href="https://www.youtube.com/@GeorgeMapleMusic"
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/60 hover:text-[var(--mm-accent)]"
            data-testid="show-youtube-channel"
          >
            More on YouTube →
          </a>
        </p>

        {/* Primary CTA — tickets first, then the manifesto + store tiles
            below so the buyer sees the buy action before exploratory links. */}
        {onSale && !soldOut && !isPrivate && (
          <div className="mb-6" data-testid="show-cta-block">
            <Link
              to={`/buy/${data.buy_slug}`}
              className="btn-line w-full justify-center text-sm py-3.5"
              data-testid="show-cta-tickets"
            >
              Get tickets &mdash; {data.currency_symbol}{Math.round(data.price)} <ArrowRight className="w-4 h-4" strokeWidth={1.4} />
            </Link>
          </div>
        )}

        {(isPrivate || (!onSale && !soldOut)) && (
          <div className="mb-6">
            <ShowInterestForm city={data.city} />
          </div>
        )}

        {soldOut && (
          <div className="border border-rose-400/40 text-rose-200 px-4 py-4 text-center mb-6" data-testid="show-sold-out">
            <p className="font-mono text-[11px] uppercase tracking-[0.32em]">Sold out</p>
            <p className="text-[12px] mt-1 opacity-75">Join the waitlist via the contact form for cancellations.</p>
          </div>
        )}

        {/* Manifesto + Store CTAs — sit beneath the buy button so the
            buyer is offered exploratory next steps after the primary CTA. */}
        <div className="flex flex-col sm:flex-row gap-3 mb-12" data-testid="show-cta-followups">
          <Link
            to="/manifesto"
            className="flex-1 border border-white/20 hover:border-[var(--mm-accent)] px-5 py-3.5 transition text-center"
            data-testid="show-link-manifesto"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 block mb-1">Read</span>
            <span className="text-[15px] text-white/90" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              The manifesto →
            </span>
          </Link>
          <Link
            to="/store"
            className="flex-1 border border-white/20 hover:border-[var(--mm-accent)] px-5 py-3.5 transition text-center"
            data-testid="show-link-store"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 block mb-1">Browse</span>
            <span className="text-[15px] text-white/90" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              The store →
            </span>
          </Link>
        </div>

        {/* Follow / engagement — direct social CTA + back-to-store loop.
            Engagement point #2. */}
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-3">Follow</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-12">
          <a
            href="https://www.instagram.com/georgemapleofficial/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between border border-white/20 hover:border-[var(--mm-accent)] px-4 py-3 transition"
            data-testid="show-instagram"
          >
            <span className="flex items-center gap-3">
              <Instagram className="w-4 h-4" strokeWidth={1.4} />
              <span className="text-[13px] text-white">@georgemapleofficial</span>
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/55">Instagram →</span>
          </a>
          <Link
            to="/manifesto"
            className="flex items-center justify-between border border-white/20 hover:border-[var(--mm-accent)] px-4 py-3 transition"
            data-testid="show-manifesto"
          >
            <span className="text-[13px] text-white">Read the manifesto</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/55">Read →</span>
          </Link>
        </div>

        {/* CTAs */}

        <p className="text-center mt-14 font-mono text-[10px] uppercase tracking-[0.32em] text-white/40">
          <Link to="/tickets" className="hover:text-white/85">View all shows</Link>
          <span className="mx-2 opacity-30">·</span>
          <Link to="/" className="hover:text-white/85">Back to Metamorph</Link>
        </p>
      </div>
    </main>
  );
}

function ShowInterestForm({ city }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("submitting");
    try {
      await api.post("/tickets/interest", {
        city,
        email: email.trim(),
        name: name.trim() || undefined,
      });
      setStatus("done");
    } catch { setStatus("error"); }
  };

  return (
    <div className="border border-white/15 px-5 py-6" data-testid="show-interest-form">
      <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-white/65 mb-1 text-center">
        Register your interest
      </p>
      <p className="text-[13px] text-white/70 text-center mb-5">
        Drop your email and we&rsquo;ll let you know first.
      </p>
      {status === "done" ? (
        <div className="flex flex-col items-center gap-2 py-3" data-testid="show-interest-done">
          <Check className="w-5 h-5 text-[var(--mm-accent-hover)]" strokeWidth={1.4} />
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/75">Noted — speak soon.</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-2.5">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name (optional)"
            className="bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-3 py-2.5 text-sm text-white font-mono tracking-wide placeholder-white/45"
            data-testid="show-interest-name"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-3 py-2.5 text-sm text-white font-mono tracking-wide placeholder-white/45"
            data-testid="show-interest-email"
            required
          />
          <button
            type="submit"
            disabled={status === "submitting" || !email.trim()}
            className="btn-line w-full justify-center disabled:opacity-30 disabled:cursor-not-allowed mt-1"
            data-testid="show-interest-submit"
          >
            {status === "submitting" ? "Sending…" : "Notify me"}
          </button>
          {status === "error" && <p className="font-mono text-[10px] text-red-400/75 text-center mt-1">Something went wrong — try again.</p>}
        </form>
      )}
    </div>
  );
}
